import { useTranslation } from '@parentpath/i18n';

interface WelcomeStepProps {
  name: string;
  onNext: () => void;
  onCancel?: () => void;
  customMessage?: string;
}

// ParentPath Logo Component
function ParentPathLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 298.37 361.74"
      className={className}
      fill="currentColor"
    >
      <path d="M142.05,0C98.22,0,50.17,4.75,0,11.62v350.12h92.41v-24.85c0-31.3,14.51-51.29,51.75-65.98,87.44-34.5,154.2-50.7,154.2-136.24S240.28,0,142.05,0ZM159.3,196.44c-10.34,4.36-26.91,8.1-38.1,9.52-27.42,3.48-46.38-11.3-48.21-37.42-1.22-12.1.54-26.48,7.38-36.58l.09-.13c9.44-14.38,28.84-14.79,42.77-21.65,14.69-6.74,19.39-23.23,29.16-34.97,2.79-3.22,6.03-6.06,9.81-8.02,5.79-3.09,13.17-3.95,19.94-3.32,81.27,9.92,34.32,107.24-22.85,132.56Z" />
    </svg>
  );
}

export function WelcomeStep({ name, onNext, onCancel, customMessage }: WelcomeStepProps) {
  const { t } = useTranslation('onboarding');
  const firstName = name.split(' ')[0];

  return (
    <div className="text-center">
      {/* Logo */}
      <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center">
        <ParentPathLogo className="w-16 h-16 text-white" />
      </div>

      {/* Title */}
      <h1 className="text-2xl font-bold text-white mb-2">
        {t('welcome.title', { name: firstName })}
      </h1>

      {/* Description */}
      <p className="text-white/70 mb-8">
        {customMessage || t('welcome.description')}
      </p>

      {/* Features Preview */}
      <div className="bg-white/10 rounded-[15px] border border-white/20 p-6 mb-8 text-left">
        <p className="text-sm font-medium text-white/60 mb-4">{t('welcome.featuresTitle')}</p>
        <ul className="space-y-3">
          <li className="flex items-center gap-3 text-white/80">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-sm font-bold">1</span>
            {t('welcome.feature1')}
          </li>
          <li className="flex items-center gap-3 text-white/80">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-sm font-bold">2</span>
            {t('welcome.feature2')}
          </li>
          <li className="flex items-center gap-3 text-white/80">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-sm font-bold">3</span>
            {t('welcome.feature3')}
          </li>
          <li className="flex items-center gap-3 text-white/80">
            <span className="w-6 h-6 bg-pp-orange/20 rounded-full flex items-center justify-center text-pp-orange text-sm font-bold">4</span>
            {t('welcome.feature4')}
          </li>
        </ul>
      </div>

      {/* CTA */}
      <button
        onClick={onNext}
        className="w-full py-3 bg-pp-orange rounded-[10px] text-white font-semibold hover:bg-pp-orange/90 transition-colors"
      >
        {t('welcome.start')}
      </button>

      {/* Cancel Button */}
      {onCancel && (
        <button
          onClick={onCancel}
          className="mt-4 text-sm text-white/50 hover:text-white/70 transition-colors"
        >
          {t('common.cancelLogout')}
        </button>
      )}
    </div>
  );
}
